/**
 * 视频转 GIF 工具
 *----------------------------------------------------
 * 采用「浏览器逐帧抽取 → gif.js.optimized 编码」的方案。
 *
 * 使用：
 *   import { convertVideoToGif, isVideoFile } from '@/utils/videoToGif';
 *   const gifFile = await convertVideoToGif(file);
 *
 * 依赖：
 *   1) npm 包 gif.js.optimized（UMD 挂 window.GIF）
 *   2) public/vendor/gif.worker.js（postinstall 从 npm 包复制过来）
 *
 * 默认参数（体积友好）：
 *   fps       10       每秒抽取的帧数
 *   maxWidth  480      GIF 最大宽度（等比缩放，短视频会保留原尺寸）
 *   maxDurationSec 10  超过该时长将截断（避免生成过大 GIF）
 *   quality   10       gif.js 的 quality 参数，值越小质量越高，10 是官方默认
 *
 * 注：超时预算沿用上层 newUpload.vue 的整体兜底（gif 管线 180s），
 *     本文件不设额外超时。
 *
 * 抽帧策略（兼容 iOS）：
 *   - 常规（Android/桌面，duration 有限）→ seek 逐帧抽取（快、帧位精确）
 *   - iOS 或 duration 为 Infinity（iPhone 拍摄的 mov/mp4 常把 moov 元数据放文件尾，
 *     iOS 对 blob URL 报 duration=Infinity，seek 不可靠且 drawImage 可能出黑帧）
 *     → 播放式抽帧：muted + playsinline 播放，按帧率边播边采样，不依赖 duration/seek。
 *     播放式抽帧与 mediaRecorderCompressor.js 已验证的 iOS 方案同款。
 *
 * 健壮性加固（非超时类）：
 *   1. <video> 挂载到 DOM（iOS/部分 WebView 不挂 DOM 不解码），转换结束后移除；
 *   2. 首帧 t=0 时 currentTime 与目标相同，部分内核不触发 seeked，直接放行；
 *   3. 支持 AbortSignal（options.signal）：中止时立即停止抽帧、abort 编码 worker，
 *      加载/抽帧阶段也监听 signal，上层整体超时触发后能立刻停止，不后台空转；
 *   4. 文件就绪等待（waitForFileReady，由 mediaCompressor 在分派前统一调用）：
 *      iOS 相册可能在导出完成前就交付文件（系统 loading 未走完相册已关闭），
 *      尾部读不到时每 2s 轮询、最长 20s，等不到抛 FILE_NOT_READY，上层不得降级上传原文件；
 *   5. 播放式抽帧启动双保险：3s 无启动信号 nudge（倍速归 1 重调 play()），
 *      15s 仍无信号快速失败（PLAYBACK_START_TIMEOUT），不再挂到上层 180s 整体超时。
 */

// 通过静态 import 让 webpack 打入 chunk，减少运行时报错风险
// gif.js.optimized 是 UMD，import 后取 default 或 window.GIF
let GIFCtor = null;

async function loadGifCtor() {
  if (GIFCtor) return GIFCtor;
  const mod = await import(/* webpackChunkName: "gif-encoder" */ 'gif.js.optimized');
  GIFCtor = mod && (mod.default || mod);
  if (!GIFCtor && typeof window !== 'undefined' && window.GIF) {
    GIFCtor = window.GIF;
  }
  if (!GIFCtor) {
    throw new Error('gif.js.optimized 加载失败');
  }
  return GIFCtor;
}

const VIDEO_MIME_PREFIX = 'video/';
const VIDEO_EXT_REG = /\.(mp4|mov|m4v|webm|avi|3gp|mkv|mpe?g|ogv|wmv|flv)$/i;

/**
 * 是否为 iOS 环境（iPhone/iPad/iPod，含微信/企微 iOS WebView）
 * @returns {boolean}
 */
function isIOSEnv() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent || '');
}

/**
 * 判断 File 是否为视频类型
 * @param {File|Blob} file
 * @returns {boolean}
 */
export function isVideoFile(file) {
  if (!file) return false;
  if (typeof file.type === 'string' && file.type.startsWith(VIDEO_MIME_PREFIX)) {
    return true;
  }
  // 部分环境（企微/微信选文件）拿到的 file.type 可能为空，按扩展名兜底
  if (typeof file.name === 'string' && VIDEO_EXT_REG.test(file.name)) {
    return true;
  }
  return false;
}

/**
 * 把原文件的名字扩展名换成 .gif
 * @param {string} name
 */
function toGifName(name) {
  if (!name || typeof name !== 'string') return 'converted.gif';
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return `${name}.gif`;
  return `${name.slice(0, idx)}.gif`;
}

/**
 * 用 URL 加载视频到 <video> 元素并等待数据就绪
 * - 视频元素挂载到 DOM（iOS/部分 WebView 不挂 DOM 不解码），隐藏定位不干扰页面；
 * - loadeddata 为主事件，readyState 达标（canplay/loadedmetadata）兜底；
 * - 监听 signal：上层整体超时触发后可立即停止，避免无限等待；
 * - rAF 帧检查兜底：iOS 对无法解析的 blob 视频（如 moov 元数据在文件尾的长视频）会
 *   停留在 networkState=3（NETWORK_NO_SOURCE）/readyState=0 且不派发 error 事件，
 *   持续停留超过阈值判定无法解析，快速失败而非干等整体超时。
 *   注意：设置 src 后同步读 networkState 为 3 是正常瞬态（Chrome 亦如此），不能同步判失败。
 * 注意：调用方负责在 finally 中把返回的 video 从 DOM 移除并 revoke URL。
 * @param {string} url
 * @param {AbortSignal} [signal]
 * @param {Object} [options]
 * @param {string|null} [options.crossOrigin='anonymous'] video.crossOrigin 取值；
 *        iOS 上 blob URL 设置 crossOrigin 可能触发 WebKit 已知加载问题，传 null 规避
 * @param {number} [options.minReadyState=2] 放行所需最低 readyState：
 *        1=HAVE_METADATA（iOS 长视频 loadeddata 可能永不触发，播放式抽帧只需 metadata）
 *        2=HAVE_CURRENT_DATA（seek 抽帧需要真实数据）
 * @returns {Promise<HTMLVideoElement>}
 */
function loadVideoElement(url, signal, options = {}) {
  // minReadyState：放行所需的最低 readyState
  //   1=HAVE_METADATA（iOS 长视频 / 播放式抽帧场景，metadata 就绪即可）
  //   2=HAVE_CURRENT_DATA（常规 seek 抽帧，需要真实数据）
  const minReadyState = options.minReadyState || 2;
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    // 避免移动端全屏播放
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.muted = true;
    // iOS 上 blob URL 设置 crossOrigin 可能触发 WebKit 已知问题导致源不可用；
    // blob 本为同源，crossOrigin 无实际作用，传 null 规避
    if (options.crossOrigin !== null) {
      video.crossOrigin = options.crossOrigin || 'anonymous';
    }
    // 必须挂到 DOM，否则 iOS 有时不解码；用绝对定位挪出可视区
    video.style.cssText = 'position:fixed;left:-99999px;top:-99999px;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(video);
    video.src = url;

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      if (signal && typeof signal.removeEventListener === 'function') {
        signal.removeEventListener('abort', onAbort);
      }
      video.removeEventListener('loadeddata', onLoaded);
      video.removeEventListener('loadedmetadata', onMetaOk);
      video.removeEventListener('canplay', onMetaOk);
      video.removeEventListener('error', onError);
    };
    const removeVideo = () => {
      try {
        if (video.parentNode) video.parentNode.removeChild(video);
      } catch (_) { /* ignore */ }
    };
    // 彻底释放解码器：仅 removeChild 不够（iOS WKWebView 上加载中的 video 会持有
    // 解码器资源，残留会污染后续所有视频加载，表现为"后续上传全部卡 0%"）
    const releaseDecoder = () => {
      try { video.removeAttribute('src'); } catch (_) { /* ignore */ }
      try { video.load(); } catch (_) { /* ignore */ }
    };
    const onLoaded = () => {
      cleanup();
      resolve(video);
    };
    const onMetaOk = () => {
      // minReadyState=1（iOS 长视频）：moov 元数据在文件尾时 iOS 对 blob URL 不主动读文件尾，
      // loadeddata 可能永不触发（readyState 停留在 1=HAVE_METADATA）。播放式抽帧只需要
      // metadata（videoWidth/Height 已就绪）即可工作，因此 iOS 场景放宽到 1 就放行；
      // 其余场景保持 2（HAVE_CURRENT_DATA），seek 抽帧需要真实数据。
      if (video.readyState >= minReadyState) onLoaded();
    };
    // 注意：onError 同时被 'error' 事件监听器（传入 Event 对象）和帧检查
    // （传入自定义 Error）调用，需区分，避免把 Event 对象 reject 给下游
    const onError = (maybeEvent) => {
      cleanup();
      removeVideo();
      releaseDecoder();
      if (maybeEvent instanceof Error) {
        reject(maybeEvent);
      } else {
        const err = new Error('视频加载失败');
        err.code = 'VIDEO_LOAD_ERROR';
        reject(err);
      }
    };
    const onAbort = () => {
      cleanup();
      removeVideo();
      releaseDecoder();
      const err = new Error('转换已取消');
      err.name = 'AbortError';
      reject(err);
    };

    // eslint-disable-next-line no-console
    console.log('[videoToGif][load] 开始加载视频', { readyState: video.readyState, networkState: video.networkState, minReadyState });
    video.addEventListener('loadeddata', onLoaded);
    video.addEventListener('loadedmetadata', onMetaOk);
    video.addEventListener('canplay', onMetaOk);
    video.addEventListener('error', onError);
    // rAF 帧检查兜底（非定时器）：正常加载会在几帧内离开 networkState=3/readyState=0
    // 状态（Chrome 实测 ~200ms 内 networkState 3→1、readyState 0→4）或被事件放行；
    // iOS 无法解析的视频会永久停留且无事件，超过阈值快速失败
    let stuckFrames = 0;
    const MAX_STUCK_FRAMES = 240; // ≈4s（60fps），正常视频远小于此
    const checkStuck = () => {
      if (settled) return;
      // 有进展（networkState 离开 3 或 readyState 提升）：事件将接管，停止检查
      if (video.networkState !== 3 || video.readyState > 0) return;
      // 已设置 error 属性（即使未派发事件）也算失败
      if (video.error) { onError(); return; }
      stuckFrames++;
      if (stuckFrames >= MAX_STUCK_FRAMES) {
        // eslint-disable-next-line no-console
        console.warn('[videoToGif][load] 视频长时间无进展（networkState=3 无事件），判定无法解析，快速失败');
        const err = new Error('无法解析视频（可能格式不受支持或文件不完整）');
        err.code = 'VIDEO_NO_SOURCE';
        onError(err);
        return;
      }
      requestAnimationFrame(checkStuck);
    };
    requestAnimationFrame(checkStuck);
    if (signal && typeof signal.addEventListener === 'function') {
      if (signal.aborted) {
        onAbort();
      } else {
        signal.addEventListener('abort', onAbort, { once: true });
      }
    }
  });
}

/**
 * 定位视频到指定时间并等待渲染完成
 * - 目标位置与当前位置相同（如首帧 t=0）时部分内核不触发 seeked，直接放行；
 * - 监听 signal：上层整体超时触发后可立即停止。
 * @param {HTMLVideoElement} video
 * @param {number} time
 * @param {AbortSignal} [signal]
 * @returns {Promise<void>}
 */
function seekVideo(video, time, signal) {
  return new Promise((resolve, reject) => {
    const target = Math.min(time, Math.max(0, video.duration - 0.001));
    if (Math.abs(video.currentTime - target) < 0.001) {
      // 位置未变化：不 seek，给 GPU 一个 tick 完成绘制后直接放行
      requestAnimationFrame(() => resolve());
      return;
    }

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      if (signal && typeof signal.removeEventListener === 'function') {
        signal.removeEventListener('abort', onAbort);
      }
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
    const onSeeked = () => {
      cleanup();
      // 给 GPU 一个 tick 完成绘制
      requestAnimationFrame(() => resolve());
    };
    const onError = () => {
      cleanup();
      const err = new Error('视频seek 失败');
      err.code = 'SEEK_ERROR';
      reject(err);
    };
    const onAbort = () => {
      cleanup();
      const err = new Error('转换已取消');
      err.name = 'AbortError';
      reject(err);
    };

    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    if (signal && typeof signal.addEventListener === 'function') {
      if (signal.aborted) {
        onAbort();
      } else {
        signal.addEventListener('abort', onAbort, { once: true });
      }
    }
    try {
      video.currentTime = target;
    } catch (e) {
      onError();
    }
  });
}

/**
 * 播放式抽帧：muted + playsinline 播放视频，按帧率边界边播边采样绘制。
 * 适用于 iOS 或 duration 为 Infinity 的视频（不依赖 duration/seek，播到结尾自动停止；
 * duration 为 Infinity 时 ended 可能不触发，但播放到真实结尾后 video.paused 会变为 true）。
 *
 * 关键兼容点：启动采样**不依赖 video.play() 的 promise** —— iOS 微信 WKWebView 在自动播放
 * 场景下 play() 的 promise 可能永远不 settle（既不 resolve 也不 reject），导致依赖它的
 * 启动逻辑永远不执行而挂起。因此以 'playing'/'timeupdate' 事件（播放真实开始后必然触发）
 * 作为启动信号，play() 的 promise 仅作辅助与失败兜底。
 *
 * @param {HTMLVideoElement} video
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} gif gif.js 实例（需要 addFrame）
 * @param {number} width
 * @param {number} height
 * @param {Object} [options]
 * @param {number} [options.fps=10]
 * @param {number} [options.maxDurationSec=10]  最多抽取的时长（秒）
 * @param {AbortSignal} [options.signal]
 * @param {(p:number)=>void} [options.onProgress]
 * @returns {Promise<void>}
 */
function captureFramesByPlayback(video, ctx, gif, width, height, options = {}) {
  const { fps = 10, maxDurationSec = 10, signal, onProgress } = options;
  return new Promise((resolve, reject) => {
    const frameInterval = 1 / fps;
    const delay = Math.round(1000 / fps);
    // 最多抽 maxDurationSec 的帧（与 seek 路径一致）
    const totalFrames = Math.max(1, Math.floor(maxDurationSec * fps));
    let captured = 0;
    let nextFrameTime = 0;
    let finished = false;
    let started = false;
    let rafId = 0;
    // 启动保护定时器（nudge 重试 / 启动超时快速失败）：提前声明，
    // cleanup 与 start 中清理，函数末尾统一设置
    let nudgeTimer = 0;
    let startTimeoutTimer = 0;
    const hasRVFC = typeof video.requestVideoFrameCallback === 'function';
    const startTs = Date.now();
    const tag = '[videoToGif][playback]';
    // 启动保护参数：3s 无信号 nudge、15s 无信号快速失败
    const NUDGE_DELAY_MS = 3 * 1000;
    const START_TIMEOUT_MS = 15 * 1000;

    const cleanup = () => {
      if (rafId) { try { cancelAnimationFrame(rafId); } catch (_) { /* ignore */ } }
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('error', onError);
      if (signal && typeof signal.removeEventListener === 'function') {
        signal.removeEventListener('abort', onAbort);
      }
      // 启动保护定时器：任何结束路径（完成/失败/取消）都清理，避免泄漏
      if (nudgeTimer) { try { clearTimeout(nudgeTimer); } catch (_) { /* ignore */ } }
      if (startTimeoutTimer) { try { clearTimeout(startTimeoutTimer); } catch (_) { /* ignore */ } }
      // 抽帧结束立即暂停，避免编码阶段视频还在后台空播
      try { video.pause(); } catch (_) { /* ignore */ }
    };
    const finish = (err) => {
      if (finished) return;
      finished = true;
      cleanup();
      if (err) {
        // eslint-disable-next-line no-console
        console.log(tag, '抽帧失败：', err && err.message, '已抽', captured, '帧，耗时', Date.now() - startTs, 'ms');
        reject(err);
      } else {
        // eslint-disable-next-line no-console
        console.log(tag, '抽帧完成：', captured, '帧，耗时', Date.now() - startTs, 'ms');
        resolve();
      }
    };
    const onEnded = () => {
      // 一帧都没抽到就 ended（视频极短/无法解码）→ 判时长无效快速失败
      if (captured === 0) finish(new Error('视频时长无效'));
      else finish();
    };
    const onPause = () => {
      // 播放被暂停（含播完自动暂停，duration=Infinity 时 ended 可能不触发）：已抽到帧即完成
      if (captured > 0) finish();
    };
    const onError = () => finish(new Error('视频播放失败'));
    const onAbort = () => {
      const err = new Error('转换已取消');
      err.name = 'AbortError';
      finish(err);
    };

    // 启动采样（幂等）：'playing' / 'timeupdate' / play() resolve 任一到达即开始
    const start = () => {
      if (finished || started) return;
      started = true;
      // 启动成功：停掉启动保护（nudge / 启动超时快速失败）定时器
      if (nudgeTimer) { try { clearTimeout(nudgeTimer); } catch (_) { /* ignore */ } }
      if (startTimeoutTimer) { try { clearTimeout(startTimeoutTimer); } catch (_) { /* ignore */ } }
      // eslint-disable-next-line no-console
      console.log(tag, '播放已开始，开始采样 fps=', fps, 'totalFrames=', totalFrames);
      tick();
    };
    const onPlaying = () => { start(); };
    const onTimeUpdate = () => {
      // play() promise 挂起时，timeupdate 证明播放确实在推进，作为启动/驱动兜底
      start();
      tick();
    };

    const tick = () => {
      if (finished || !started) return;
      // 播到真实结尾：duration 为 Infinity 时 ended 可能不触发，播放结束后会 paused
      if (video.ended) { onEnded(); return; }
      if (captured > 0 && video.paused) { finish(); return; }

      const t = video.currentTime;
      if (captured < totalFrames && t >= nextFrameTime) {
        try {
          ctx.drawImage(video, 0, 0, width, height);
          gif.addFrame(ctx, { copy: true, delay });
          captured++;
          nextFrameTime += frameInterval;
          if (typeof onProgress === 'function') {
            // 抽帧阶段占前60%
            try { onProgress((captured / totalFrames) * 0.6); } catch (_) { /* ignore */ }
          }
        } catch (_) {
          // 单帧绘制失败跳过，不中断整段（个别 iOS 帧未就绪时可能抛错）
        }
      }
      if (captured >= totalFrames) { finish(); return; }
      // 优先用 requestVideoFrameCallback（专为视频帧同步设计，iOS 16+），退化到 rAF；
      // timeupdate 事件同时作为兜底驱动（tick 幂等，重复调用无副作用）
      if (hasRVFC) {
        video.requestVideoFrameCallback(tick);
      } else {
        rafId = requestAnimationFrame(tick);
      }
    };

    video.addEventListener('ended', onEnded, { once: true });
    video.addEventListener('pause', onPause);
    video.addEventListener('playing', onPlaying, { once: true });
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('error', onError, { once: true });
    if (signal && typeof signal.addEventListener === 'function') {
      if (signal.aborted) { onAbort(); return; }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    // 冗余 autoplay 设置：提高 iOS WKWebView muted autoplay 通过率
    try { video.muted = true; } catch (_) { /* ignore */ }
    try { video.defaultMuted = true; } catch (_) { /* ignore */ }
    try { video.setAttribute('muted', ''); } catch (_) { /* ignore */ }
    try { video.setAttribute('playsinline', 'true'); } catch (_) { /* ignore */ }
    try { video.setAttribute('webkit-playsinline', 'true'); } catch (_) { /* ignore */ }
    // 加速播放缩短等待：iOS 最高 2x，其余浏览器可 4x（与 mediaRecorderCompressor 一致）
    try { video.playbackRate = 4; } catch (_) { /* ignore */ }
    // 关键：不依赖 play() 的 promise（iOS WKWebView 上可能永远不 settle 导致挂起），
    // 仅作辅助信号与失败兜底；启动以 'playing'/'timeupdate' 事件为准
    // eslint-disable-next-line no-console
    console.log(tag, '调用 video.play()');
    let playPromise = null;
    try { playPromise = video.play(); } catch (_) { /* ignore */ }
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(() => { start(); }).catch((e) => {
        // play() 被拒（如手势策略限制）：'playing' 未触发说明确实没播，快速失败
        // eslint-disable-next-line no-console
        console.log(tag, 'video.play() 被拒：', e && e.message ? e.message : e);
        finish(new Error('视频播放失败：' + (e && e.message ? e.message : e)));
      });
    }

    // ===== 启动保护（nudge + 快速失败）=====
    // iOS 企微 WKWebView 自动播放策略下 play() 可能既不 resolve 也不 reject，
    // 'playing'/'timeupdate' 也迟迟不来 → 旧实现会一路挂到上层 180s 整体超时。
    // - nudge：3s 无启动信号，倍速归 1 再调一次 play()。部分内核对高倍速自动播放
    //   更苛刻，第二次常规速调用大概率放行；
    // - 快速失败：15s 仍无启动信号，抛 PLAYBACK_START_TIMEOUT，上层按压缩失败降级，
    //   不再让用户傻等。
    nudgeTimer = setTimeout(() => {
      if (finished || started) return;
      // eslint-disable-next-line no-console
      console.warn(tag, '3s 无启动信号，nudge：倍速归1重调 play()');
      try { video.playbackRate = 1; } catch (_) { /* ignore */ }
      try {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => { /* nudge 失败交由启动超时判定 */ });
      } catch (_) { /* ignore */ }
    }, NUDGE_DELAY_MS);
    startTimeoutTimer = setTimeout(() => {
      if (finished || started) return;
      // eslint-disable-next-line no-console
      console.warn(tag, '15s 无启动信号，快速失败');
      const err = new Error('视频播放启动超时');
      err.code = 'PLAYBACK_START_TIMEOUT';
      finish(err);
    }, START_TIMEOUT_MS);
  });
}

/**
 * ratio (0~1] → 一组 GIF 编码参数（越大越清晰、体积越大）
 *   fps      5 ~ 15   帧率
 *   maxWidth 240 ~ 720  最大宽度（等比缩放）
 *   quality  20 ~ 5    gif.js 的 quality（数值越小画面越好，官方默认 10）
 * @param {number} ratio
 */
function ratioToGifPreset(ratio) {
  const r = Math.max(0, Math.min(1, Number(ratio) || 0));
  return {
    fps: Math.round(5 + 10 * r),
    maxWidth: Math.round(240 + 480 * r),
    // 反向映射：ratio 越大 quality 数值越小（画质越好）
    quality: Math.max(1, Math.round(20 - 15 * r)),
  };
}

/**
 * 单次就绪探测：尾部 64 字节可读且 size>0 视为就绪。
 * - size=0：文件还在创建中（iOS 相册导出未完成）或确实是空文件；
 * - 尾部读不到：视频 moov 元数据常在文件尾，导出仍在写入时尾部读取失败/挂起。
 * @param {File|Blob} file
 * @param {number} probeTimeoutMs 单次探测超时（防尾部读取永久挂起）
 * @returns {Promise<boolean>}
 */
async function probeFileReady(file, probeTimeoutMs) {
  try {
    if (!file || !file.size) return false;
    if (typeof file.slice !== 'function') return false;
    // 尾部读取加超时保护：iOS 对写入中的文件，read 可能长时间不 settle，
    // 不设超时会让整个等待循环卡死在单次探测上
    const tailPromise = file.slice(Math.max(0, file.size - 64), file.size).arrayBuffer();
    const tail = await Promise.race([
      tailPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('probe timeout')), probeTimeoutMs)),
    ]);
    return !!tail && tail.byteLength > 0;
  } catch (_) {
    return false;
  }
}

/**
 * 等待文件就绪（iOS 相册导出竞态兜底），最长 20s。
 *----------------------------------------------------
 * iOS 相册（PHPicker / WKWebView file input）可能在系统导出/转码完成前就关闭相册并
 * 交付文件（用户表现：相册自带 loading 未走完相册就关了）。此时文件 size=0 或尾部
 * 不可读，交给任何转换管线都会永久卡死；且降级上传原文件同样是坏文件。
 *
 * 策略：
 *   1. 先做一次即时探测，就绪立即返回 —— 常规路径零等待零开销；
 *   2. 未就绪才进入轮询模式（onStage('prepare') 通知上层展示等待文案），
 *      每 pollMs 重查一次；iOS 关闭相册后系统会在后台继续写完导出文件；
 *   3. 持续 size=0 趷过宽限期判定为死文件，提前抛 EMPTY_FILE，不等满 20s；
 *   4. 超过 timeoutMs（默认 20s）仍未就绪抛 FILE_NOT_READY。
 *      两个错误均带 silent 标记：compressItemMedia 内已负责 Toast，外层 catch
 *      依 silent 去重；且上层不得降级上传原文件（原文件同样不完整）。
 *
 * @param {File|Blob} file
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {(stage:string)=>void} [options.onStage] 仅在进入轮询模式时回调一次 'prepare'
 * @param {number} [options.timeoutMs=20000] 最长等待上限（20s）
 * @param {number} [options.pollMs=2000] 轮询间隔
 * @param {number} [options.probeTimeoutMs=3000] 单次探测超时
 * @returns {Promise<void>} 就绪时 resolve
 */
export async function waitForFileReady(file, options = {}) {
  const {
    signal,
    onStage,
    timeoutMs = 20 * 1000,
    pollMs = 2 * 1000,
    probeTimeoutMs = 3 * 1000,
    emptyFileGraceMs = 6 * 1000,
  } = options;

  const throwIfAborted = () => {
    if (signal && signal.aborted) {
      const err = new Error('转换已取消');
      err.name = 'AbortError';
      throw err;
    }
  };

  // 1. 即时探测：就绪直接返回（绝大多数文件的路径，零开销）
  throwIfAborted();
  // eslint-disable-next-line no-await-in-loop
  if (await probeFileReady(file, probeTimeoutMs)) return;

  // 2. 未就绪 → 轮询模式：通知上层展示"准备中"文案
  try { onStage && onStage('prepare'); } catch (_) { /* ignore */ }
  // eslint-disable-next-line no-console
  console.warn('[videoToGif] 文件未就绪（尾部不可读或 size=0），进入就绪等待（最长20s）', {
    size: file && file.size,
    name: file && file.name,
  });

  const startTs = Date.now();
  let everHadSize = !!(file && file.size);
  while (Date.now() - startTs < timeoutMs) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, pollMs));
    throwIfAborted();
    if (file && file.size > 0) everHadSize = true;
    // eslint-disable-next-line no-await-in-loop
    if (await probeFileReady(file, probeTimeoutMs)) {
      // eslint-disable-next-line no-console
      console.log('[videoToGif] 文件已就绪，等待耗时', Date.now() - startTs, 'ms');
      return;
    }
    // 持续空文件超过宽限期：死文件（非"正在创建"），没必要等满 20s
    if (!everHadSize && Date.now() - startTs >= emptyFileGraceMs) {
      const err = new Error('文件内容为空，无法转换');
      err.code = 'EMPTY_FILE';
      err.silent = true;
      throw err;
    }
  }
  const err = new Error('视频未就绪（相册导出未完成），请重新选择');
  err.code = 'FILE_NOT_READY';
  err.silent = true;
  throw err;
}

/**
 * 视频转 GIF 主函数
 * @param {File} file 原始视频文件
 * @param {Object} [options]
 * @param {number} [options.ratio=0.6]     压缩比例（越大越清晰），会被下面的显式参数覆盖
 * @param {number} [options.fps]           显式指定帧率（若提供则忽略 ratio 推导值）
 * @param {number} [options.maxWidth]      显式指定最大宽度（若提供则忽略 ratio 推导值）
 * @param {number} [options.quality]       显式指定 gif.js quality（若提供则忽略 ratio 推导值）
 * @param {number} [options.maxDurationSec=10]  时长保护上限，超过将截断
 * @param {string} [options.workerScript='/vendor/gif.worker.js']
 * @param {(progress: number) => void} [options.onProgress] 0~1
 * @param {AbortSignal} [options.signal]   取消信号（可选）：中止后立即停止抽帧并 abort 编码 worker
 * @returns {Promise<File>} 转换后的 GIF File
 */
export async function convertVideoToGif(file, options = {}) {
  if (!file) throw new Error('文件为空');
  if (!isVideoFile(file)) throw new Error('非视频文件，无需转换');
  // 文件就绪等待（iOS 相册导出竞态兜底）：文件未就绪（size=0 / 尾部不可读）时
// 轮询等待（最长 20s），等不到抛 FILE_NOT_READY / EMPTY_FILE —— 此时原文件同样
  // 不完整，上层不得降级上传原文件。上层 mediaCompressor 已在分派前等待过，
  // 此处通常首探即过（零等待），仅作为直连调用的兜底。
  await waitForFileReady(file, { signal: options.signal });

  const {
    ratio = 0.6,
    fps: fpsOpt,
    maxWidth: maxWidthOpt,
    quality: qualityOpt,
    maxDurationSec = 10,
    workerScript = '/vendor/gif.worker.js',
    onProgress,
    signal,
  } = options || {};

  const throwIfAborted = () => {
    if (signal && signal.aborted) {
      const err = new Error('转换已取消');
      err.name = 'AbortError';
      throw err;
    }
  };

  // 优先使用显式参数，否则从 ratio 推导
  const preset = ratioToGifPreset(ratio);
  const fps = typeof fpsOpt === 'number' ? fpsOpt : preset.fps;
  const maxWidth = typeof maxWidthOpt === 'number' ? maxWidthOpt : preset.maxWidth;
  const quality = typeof qualityOpt === 'number' ? qualityOpt : preset.quality;

  throwIfAborted();

  const GIF = await loadGifCtor();
  throwIfAborted();

  const url = URL.createObjectURL(file);
  let video = null;
  const gifStartTs = Date.now();
  try {
    // iOS：blob URL 不设 crossOrigin（规避 WebKit 已知加载问题）；
    //       minReadyState=1（长视频 moov 在文件尾时 loadeddata 可能永不触发，metadata 即放行）
    // 非 iOS：保持原行为（crossOrigin='anonymous'、minReadyState=2，seek 抽帧需要真实数据）
    video = await loadVideoElement(url, signal, {
      crossOrigin: isIOSEnv() ? null : 'anonymous',
      minReadyState: isIOSEnv() ? 1 : 2,
    });
    throwIfAborted();

    const srcW = video.videoWidth || 0;
    const srcH = video.videoHeight || 0;
    if (!srcW || !srcH) {
      throw new Error('无法读取视频尺寸');
    }

    // eslint-disable-next-line no-console
    console.log('[videoToGif] 视频加载完成', {
      name: file.name,
      size: file.size,
      type: file.type,
      duration: video.duration,
      videoWidth: srcW,
      videoHeight: srcH,
      costMs: Date.now() - gifStartTs,
    });

    const scale = srcW > maxWidth ? maxWidth / srcW : 1;
    // 保证偶数宽高，某些编码器/canvas 更稳
    const width = Math.max(2, Math.round(srcW * scale) & ~1);
    const height = Math.max(2, Math.round(srcH * scale) & ~1);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    const gif = new GIF({
      workers: 2,
      quality,
      width,
      height,
      workerScript,
      // 透明处理关闭：视频转 GIF 无透明诉求，关闭可减小体积
      transparent: null,
      // 让编码器在 worker 里跑
      dither: false,
    });

    // ===== 抽帧策略 =====
    // iOS（iPhone 拍摄的 mov/mp4 常把 moov 元数据放文件尾，iOS 对 blob URL 报
    // duration=Infinity，seek 不可靠且 drawImage 可能出黑帧）或 duration 拿不到
    // 有限值（Chrome 能解析 moov 而 iOS 不能）→ 播放式抽帧，不依赖 duration/seek。
    // 其余环境（Android/桌面，duration 有限）→ 保留 seek 逐帧抽取（快、帧位精确）。
    const isIOS = isIOSEnv();

    let duration = video.duration;
    if (!isFinite(duration) && video.seekable && video.seekable.length > 0) {
      const end = video.seekable.end(video.seekable.length - 1);
      if (isFinite(end) && end > 0) duration = end;
    }
    const durationFinite = isFinite(duration) && duration > 0;
    const usePlayback = !durationFinite || isIOS;

    // eslint-disable-next-line no-console
    console.log('[videoToGif] 抽帧策略 =', usePlayback ? 'playback（播放式）' : 'seek（逐帧seek）', {
      duration: video.duration,
      durationFinite,
      isIOS,
    });

    if (usePlayback) {
      // 播放式抽帧：最多抽 maxDurationSec 的帧；结束后进入统一编码阶段
      await captureFramesByPlayback(video, ctx, gif, width, height, {
        fps,
        maxDurationSec,
        signal,
        onProgress,
      });
    } else {
      // seek 式抽帧
      const seekDuration = Math.min(duration, maxDurationSec);
      const frameInterval = 1 / fps;
      const totalFrames = Math.max(1, Math.floor(seekDuration * fps));
      // eslint-disable-next-line no-console
      console.log('[videoToGif][seek] 开始抽帧 totalFrames=', totalFrames, 'duration=', seekDuration);
      for (let i = 0; i < totalFrames; i++) {
        throwIfAborted();
        const t = Math.min(seekDuration, i * frameInterval);
        // eslint-disable-next-line no-await-in-loop
        await seekVideo(video, t, signal);
        ctx.drawImage(video, 0, 0, width, height);
        gif.addFrame(ctx, { copy: true, delay: Math.round(1000 / fps) });
        if (typeof onProgress === 'function') {
          // 抽帧阶段占前60%
          try { onProgress((i + 1) / totalFrames * 0.6); } catch (_) { /* ignore */ }
        }
      }
      // eslint-disable-next-line no-console
      console.log('[videoToGif][seek] 抽帧完成', totalFrames, '帧，耗时', Date.now() - gifStartTs, 'ms');
    }

    // 编码：监听 signal，中止时主动 abort worker 快速失败；
    // 无额外超时（整体超时兜底沿用上层 newUpload.vue 的配置）
    const gifBlob = await new Promise((resolve, reject) => {
      let failed = false;

      const removeAbortListener = () => {
        if (signal && typeof signal.removeEventListener === 'function') {
          signal.removeEventListener('abort', onExternalAbort);
        }
      };
      const fail = (err) => {
        if (failed) return;
        failed = true;
        removeAbortListener();
        try { gif.abort(); } catch (_) { /* ignore */ }
        reject(err);
      };
      const onExternalAbort = () => {
        const err = new Error('转换已取消');
        err.name = 'AbortError';
        fail(err);
      };

      gif.on('finished', (blob) => {
        removeAbortListener();
        resolve(blob);
      });
      gif.on('progress', (p) => {
        if (typeof onProgress === 'function') {
          // 编码阶段占后 40%
          try { onProgress(0.6 + p * 0.4); } catch (_) { /* ignore */ }
        }
      });
      // gif.abort() 会触发本事件：外部 signal 取消时用其快速失败（fail 内部有幂等保护）
      gif.on('abort', () => {
        if (signal && signal.aborted) onExternalAbort();
      });

      if (signal && typeof signal.addEventListener === 'function') {
        if (signal.aborted) {
          onExternalAbort();
        } else {
          signal.addEventListener('abort', onExternalAbort, { once: true });
        }
      }
      if (failed) return; // signal 已处于中止状态
      try {
        gif.render();
      } catch (e) {
        fail(e);
      }
    });
    throwIfAborted();

    const gifName = toGifName(file.name);
    // 尽量返回 File，兼容性差的环境退化为 Blob（下游代码只用 name/size/type）
    let gifFile;
    try {
      gifFile = new File([gifBlob], gifName, { type: 'image/gif' });
    } catch (_) {
      gifFile = gifBlob;
      gifFile.name = gifName;
      gifFile.type = 'image/gif';
    }
    // eslint-disable-next-line no-console
    console.log('[videoToGif] 编码完成', {
      name: gifFile.name,
      size: gifFile.size,
      totalCostMs: Date.now() - gifStartTs,
    });
    return gifFile;
  } finally {
    // 释放资源：revoke blob URL + 从 DOM 移除 video + 清空 src 释放解码器
    try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
    if (video) {
      try { video.pause(); } catch (_) { /* ignore */ }
      try { video.removeAttribute('src'); video.load(); } catch (_) { /* ignore */ }
      try { if (video.parentNode) video.parentNode.removeChild(video); } catch (_) { /* ignore */ }
    }
  }
}

export default convertVideoToGif;
